import { CallingPeriodDetailsType } from '@/apis/callingstatusDateRange';
import { isTemplatePlaceholder } from '@/types';
import {
  FlatTreeNode,
  getValueAtPathFromFlatNode,
  isFlatSearchTreeLeafNode,
} from '@components/LoadSearchTemplateDialog/flatSearchTree';
import {
  TASK_CHOICE_AUTOCOMPLETE_OPTIONS,
  TASK_KIND_AUTOCOMPLETE_OPTIONS,
} from '@/constants/filterCriteria/tasks';
import {
  ACTIVITY_OPTIONS_WITH_IDS,
  ACTIVITY_STATUSES_WITH_IDS,
} from '@/constants/filterCriteria/engagement';
import { MEMBERSHIP_STATUSES_WITH_IDS } from '@/constants/filterCriteria/membership';
import { AllPlaceholderKeys } from '@components/LoadSearchTemplateDialog/types';

/** Extract initial value from node for Restart pre-fill. Returns undefined if not a leaf or value is placeholder. */
export function getSimpleInitialValue(node: FlatTreeNode | undefined, field: string): unknown {
  if (!node || !isFlatSearchTreeLeafNode(node)) return undefined;
  const raw = getValueAtPathFromFlatNode(node, field);
  if (raw === undefined || isTemplatePlaceholder(raw)) return undefined;
  return raw;
}

/**
 * For callingPeriodQuantity, the value is stored as two sibling keys (callingPeriodQuantity,
 * callingPeriodDetails) in callingStatusDateRange. getValueAtPathFromFlatNode only returns
 * the number. We need to read the parent object and construct the combined value.
 */
export function getCallingPeriodQuantityInitialValue(
  node: FlatTreeNode | undefined
): { callingPeriodQuantity: number; callingPeriodDetails: CallingPeriodDetailsType } | undefined {
  if (!node || !isFlatSearchTreeLeafNode(node)) return undefined;
  const dateRange = getValueAtPathFromFlatNode(node, 'callingStatus.callingStatusDateRange') as
    | Record<string, unknown>
    | undefined;
  if (!dateRange || typeof dateRange !== 'object') return undefined;
  const qty = dateRange.callingPeriodQuantity;
  const details = dateRange.callingPeriodDetails;
  if (
    qty === undefined ||
    isTemplatePlaceholder(qty) ||
    details === undefined ||
    isTemplatePlaceholder(details)
  ) {
    return undefined;
  }
  const numQty = typeof qty === 'number' ? qty : parseInt(String(qty));
  if (Number.isNaN(numQty) || numQty <= 0) return undefined;
  const validDetails = ['days', 'weeks', 'months', 'years'].includes(String(details))
    ? (details as CallingPeriodDetailsType)
    : 'weeks';
  return { callingPeriodQuantity: numQty, callingPeriodDetails: validDetails };
}

/** Match ids from tree to preLoadedOptions for Restart pre-fill */
export function getMatchedInitialValue(
  filterNameAndField: AllPlaceholderKeys,
  simpleInitialValue: unknown
): { id: number; label: string }[] | undefined {
  const ids = simpleInitialValue;
  if (!ids || !Array.isArray(ids) || ids.length === 0) return undefined;
  const optionsMap: Record<string, { id: number | string; label: string }[]> = {
    'membership.statusIds': MEMBERSHIP_STATUSES_WITH_IDS,
    'engagementHistory.activityIds': ACTIVITY_OPTIONS_WITH_IDS,
    'engagementHistory.activityStatusIds': ACTIVITY_STATUSES_WITH_IDS,
    'task.kinds': TASK_KIND_AUTOCOMPLETE_OPTIONS,
    'task.choices': TASK_CHOICE_AUTOCOMPLETE_OPTIONS,
  };
  const options = optionsMap[filterNameAndField];
  if (!options) return undefined;
  const idSet = new Set(ids.map((x) => (typeof x === 'string' ? x : String(x))));
  return options.filter((o) => idSet.has(typeof o.id === 'string' ? o.id : String(o.id))) as {
    id: number;
    label: string;
  }[];
}
